import AddIcon from '@mui/icons-material/Add';
import {Box, Button, FormHelperText, InputLabel, Popover, Stack} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {visuallyHidden} from '@mui/utils';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import {Placeholder, UndoRedo} from '@tiptap/extensions';
import {EditorContent, useEditor} from '@tiptap/react';
import {Slice} from '@tiptap/pm/model';
import {useEffect, useId, useMemo, useRef, useState, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import type {AvailableVariable} from '../../../config/nodeVariables';
import type {EmailPlaceholder} from '../../../config/emailPlaceholders';
import AutomationVariableNode, {AutomationVariableContext} from './AutomationVariableNode';
import AutomationVariablePicker from './AutomationVariablePicker';
import {automationTextDocument, serializeAutomationText} from './automationVariableText';
import AutomationPlaceholderNode from './AutomationPlaceholderNode';
import AutomationVariableSlashCommand, {automationVariableSlashKey} from './AutomationVariableSlashCommand';
import {automationOptionContent, referenceOptions, type AutomationVariableOption} from './automationVariableOptions';
import AutomationHtmlToolbar from './AutomationHtmlToolbar';
import {replaceAutomationText} from './htmlSourceFormatting';

interface TextFieldProps {
    label: string;
    hideLabel?: boolean;
    value: string;
    onChange: (value: string) => void;
    variables: AvailableVariable[];
    multiline?: boolean;
    rows?: number;
    /** A bounded editing viewport; long content scrolls inside the field. */
    editorHeight?: number | string;
    size?: 'small' | 'medium';
    /** HTML source uses a code font and suppresses slash suggestions inside tags. */
    htmlSource?: boolean;
    /** Alternate rendering of the HTML source, while keeping the editor and history mounted. */
    sourcePreview?: ReactNode;
    placeholder?: string;
    helperText?: string;
    disabled?: boolean;
}

type Props = TextFieldProps & (
    | {insertion: 'placeholder'; placeholders: EmailPlaceholder[]}
    | {insertion: 'step-value'; placeholders?: never}
    /** No picker and no slash menu; references already in the value still show as chips. */
    | {insertion: 'none'; placeholders?: never}
);

const TextDocument = Document.extend({content: 'paragraph'});
const TextParagraph = Paragraph.extend({whitespace: 'pre'});
const noPlaceholders: EmailPlaceholder[] = [];

/** A text editor with variable atoms, not a rich-text/HTML editor. Its only output is the source string. */
export default function AutomationTextField({
    label, hideLabel = false, value, onChange, variables, insertion, placeholders = noPlaceholders,
    multiline = false, rows = 4, editorHeight, size = 'small', htmlSource = false, sourcePreview, placeholder, helperText, disabled = false,
}: Props) {
    const {t} = useTranslation();
    const id = useId();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const selection = useRef<{from: number; to: number} | null>(null);
    const context = useMemo(() => ({variables, placeholders, disabled}), [variables, placeholders, disabled]);
    const withPlaceholders = insertion === 'placeholder';
    const withPicker = insertion !== 'none';
    const purpose = withPlaceholders ? 'insert-placeholder' : 'select-step-value';
    const options = useMemo<AutomationVariableOption[]>(() => withPlaceholders
        ? Array.from(new Map(placeholders.filter(entry => entry.key.trim()).map(entry => [entry.key, entry])).values())
            .map(entry => ({kind: 'placeholder', placeholder: entry}))
        : referenceOptions(variables), [withPlaceholders, placeholders, variables]);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const editor = useEditor({
        extensions: [
            TextDocument, TextParagraph, Text, HardBreak, UndoRedo, AutomationVariableNode,
            ...(withPlaceholders ? [AutomationPlaceholderNode] : []),
            Placeholder.configure({placeholder}),
            ...(withPicker ? [AutomationVariableSlashCommand.configure({getOptions: () => optionsRef.current, purpose, htmlSource})] : []),
        ],
        content: automationTextDocument(value, {placeholders: withPlaceholders}),
        editable: !disabled,
        shouldRerenderOnTransaction: false,
        onUpdate: ({editor: instance}) => onChange(serializeAutomationText(instance.getJSON())),
        editorProps: {
            attributes: {
                id, role: 'textbox', 'aria-labelledby': `${id}-label`,
                'aria-multiline': String(multiline), 'aria-readonly': String(disabled),
                ...(helperText ? {'aria-describedby': `${id}-help`} : {}),
            },
            handleKeyDown: (view, event) => {
                if (automationVariableSlashKey.getState(view.state)?.active) return false;
                if (event.key !== 'Enter' || event.isComposing) return false;
                if (multiline) view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.hardBreak.create()).scrollIntoView());
                return true;
            },
            handlePaste: (view, event) => {
                const pasted = event.clipboardData?.getData('text/plain');
                if (pasted === undefined) return false;
                const text = multiline ? pasted : pasted.replace(/[\r\n]+/g, ' ');
                const doc = view.state.schema.nodeFromJSON(automationTextDocument(text, {placeholders: withPlaceholders}));
                view.dispatch(view.state.tr.replaceSelection(new Slice(doc.firstChild!.content, 0, 0)).scrollIntoView());
                return true;
            },
        },
    }, [insertion, multiline, htmlSource]);

    useEffect(() => {
        if (editor && serializeAutomationText(editor.getJSON()) !== value) {
            replaceAutomationText(editor, value, withPlaceholders);
        }
    }, [editor, value, withPlaceholders]);

    useEffect(() => { editor?.setEditable(!disabled, false); }, [editor, disabled]);

    const closePicker = () => {
        setAnchor(null);
        editor?.commands.focus();
    };

    const openPicker = (element: HTMLElement) => {
        if (!editor) return;
        selection.current = {from: editor.state.selection.from, to: editor.state.selection.to};
        setAnchor(element);
    };

    return (
        <Stack gap={0.5} sx={{minWidth: 0}}>
            <InputLabel id={`${id}-label`} htmlFor={id} shrink
                        sx={hideLabel ? visuallyHidden : {mb: 0.5, transform: 'none', fontSize: '0.875rem', fontWeight: 500}}>
                {label}
            </InputLabel>
            <Box sx={theme => ({
                borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.045), opacity: disabled ? 0.6 : 1,
                '&:focus-within': {outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1},
                '& .tiptap': {
                    px: 1.75, py: multiline ? size === 'small' ? 1 : 2 : 0,
                    // Inline chips have their own padding; center the line in a fixed input height.
                    display: multiline ? 'block' : 'flex', alignItems: 'center',
                    height: multiline ? editorHeight : size === 'small' ? 40 : 56,
                    minHeight: multiline && !editorHeight ? `calc(${rows * 1.4375}em + ${size === 'small' ? 16 : 32}px)` : undefined,
                    maxHeight: editorHeight,
                    boxSizing: 'border-box', outline: 'none',
                    fontFamily: htmlSource ? '"Roboto Mono", "SFMono-Regular", Consolas, monospace' : theme.typography.fontFamily,
                    fontSize: htmlSource ? '0.8125rem' : size === 'small' ? theme.typography.body1.fontSize : '1rem', lineHeight: 1.4375,
                    whiteSpace: multiline ? 'pre-wrap' : 'pre', overflowWrap: multiline ? 'anywhere' : 'normal',
                    overflow: 'auto', overscrollBehavior: 'contain',
                    '& p': {m: 0, ...(!multiline && {flexShrink: 0, minWidth: '100%'})},
                    '& p.is-editor-empty:first-child::before': {
                        content: 'attr(data-placeholder)', position: 'absolute', color: 'text.secondary', pointerEvents: 'none',
                    },
                    '& ::selection': {bgcolor: alpha(theme.palette.primary.main, 0.18)},
                },
            })}>
                {htmlSource && <AutomationHtmlToolbar editor={editor} disabled={disabled} preview={!!sourcePreview} onInsertVariable={openPicker}/>}
                <Box sx={{display: 'grid', minWidth: 0}}>
                    <Box aria-hidden={!!sourcePreview} sx={{gridArea: '1 / 1', minWidth: 0, visibility: sourcePreview ? 'hidden' : 'visible'}}>
                        <AutomationVariableContext.Provider value={context}>
                            <EditorContent editor={editor}/>
                        </AutomationVariableContext.Provider>
                    </Box>
                    {sourcePreview && <Box sx={{gridArea: '1 / 1', minWidth: 0}}>{sourcePreview}</Box>}
                </Box>
            </Box>
            {helperText && <FormHelperText id={`${id}-help`} sx={{mx: 1.75}}>{helperText}</FormHelperText>}
            {withPicker && !disabled && !htmlSource && <Button size={'small'} color={'inherit'} startIcon={<AddIcon/>}
                                  disabled={!editor} sx={{alignSelf: 'flex-start', color: 'text.secondary'}}
                                  onMouseDown={event => event.preventDefault()}
                                  onClick={event => openPicker(event.currentTarget)}>
                {t('Insert variable')}
            </Button>}
            <Popover open={withPicker && !disabled && !!anchor} anchorEl={anchor} onClose={closePicker}
                     disableRestoreFocus transitionDuration={0}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                     slotProps={{paper: {sx: {mt: 0.5, borderRadius: 2}}}}>
                <AutomationVariablePicker options={options} purpose={purpose} onSelect={option => {
                    if (!editor) return;
                    editor.chain().focus().insertContentAt(selection.current ?? editor.state.selection, automationOptionContent(option)).run();
                    setAnchor(null);
                }}/>
            </Popover>
        </Stack>
    );
}
